'use client'

import { useEffect, useState } from 'react'
import { formatTime, zoneAbbr } from '@/lib/tournaments'
import { useLocalTimezone } from '@/lib/use-local-timezone'

/**
 * The viewer's current local time, e.g. "2:35 PM GMT+3" — the reference clock
 * for the event times shown around the site, which are localized to this same
 * zone.
 *
 * Renders nothing until after mount: the server has no idea what zone (or
 * minute) the viewer is in, so there is no correct value to prerender, and
 * emitting one would hydrate as a mismatch.
 */
export function LocalClock({ className = '' }: { className?: string }) {
  const tz = useLocalTimezone()
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
    // Tick on the minute boundary rather than every 60s from mount, so the
    // displayed minute flips when the viewer's clock does.
    let interval: ReturnType<typeof setInterval> | undefined
    const timeout = setTimeout(
      () => {
        setNow(new Date())
        interval = setInterval(() => setNow(new Date()), 60_000)
      },
      (60 - new Date().getSeconds()) * 1000
    )
    return () => {
      clearTimeout(timeout)
      if (interval) clearInterval(interval)
    }
  }, [])

  if (!now) return null

  const iso = now.toISOString()
  return (
    <span
      className={className}
      suppressHydrationWarning
      title={`Times on this page are shown in your local timezone (${tz ?? 'UTC'})`}
    >
      {formatTime(iso, tz)} {zoneAbbr(iso, tz)}
    </span>
  )
}
